import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Divider,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useClients, useSearchClients } from "../../hooks/useClient";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import { TablePagination, SortOption, SortConfig } from "../../components/common/pagination";
import { ClientsSortField } from "../../models/enums/ClientsSortField";
import { useSortState } from "../../hooks/useSortState";

const ClientsList: React.FC = () => {
  const { t } = useTranslation();
  const { navigateByEnvironment } = useEnvironmentNavigation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchError, setSearchError] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Sort state (shared hook owns the lowercase<->Asc/Desc conversion)
  const { sortField, sortDirection, currentSort, applySort } = useSortState();

  // Filter states
  const [selectedOnboardingStatus, setSelectedOnboardingStatus] = useState<string>("");
  const [selectedMatchingStatus, setSelectedMatchingStatus] = useState<string>("");

  // Use the original clients hook for initial loading (before search)
  const { data: initialData, isLoading: isLoadingInitial, error: initialError } = useClients(page, pageSize, sortField, sortDirection);

  // Use the search hook
  const {
    data: searchData,
    isLoading: isSearching,
    error: searchApiError,
    isSearched,
    search: searchClients,
    resetSearch,
  } = useSearchClients();

  // Combined data and loading state
  const data = isSearched ? searchData : initialData;
  const isLoading = isLoadingInitial || isSearching;
  const error = isSearched ? searchApiError : initialError;

  // Determine if search is possible
  const canSearch = useMemo(() => {
    return (
      searchTerm.trim().length >= 2 ||
      selectedOnboardingStatus !== "" ||
      selectedMatchingStatus !== ""
    );
  }, [searchTerm, selectedOnboardingStatus, selectedMatchingStatus]);

  // Effect to reset search when all criteria are cleared
  useEffect(() => {
    if (
      isSearched &&
      searchTerm.trim() === "" &&
      selectedOnboardingStatus === "" &&
      selectedMatchingStatus === ""
    ) {
      resetSearch();
    }
  }, [searchTerm, isSearched, resetSearch, selectedOnboardingStatus, selectedMatchingStatus]);

  const clients = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 0;

  const handleViewClient = (userId: number) => {
    navigateByEnvironment(`/clients/${userId}`);
  };

  const handleEditClient = (userId: number) => {
    navigateByEnvironment(`/clients/${userId}/edit`);
  };

  const handleSearch = () => {
    setSearchError("");

    if (!canSearch) {
      setSearchError(t("client.noSearchCriteria"));
      return;
    }

    setPage(1);
    const onboardingCompleted = selectedOnboardingStatus === ""
      ? undefined
      : selectedOnboardingStatus === "completed";
    const hasMatchings = selectedMatchingStatus === ""
      ? undefined
      : selectedMatchingStatus === "with";

    searchClients(searchTerm, 1, pageSize, onboardingCompleted, undefined, sortField, sortDirection, hasMatchings);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedOnboardingStatus("");
    setSelectedMatchingStatus("");
    setSearchError("");
    setPage(1);
    resetSearch();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleSortChange = (sort: SortConfig) => {
    applySort(sort);
    setPage(1);
  };

  // Re-run the active search whenever paging or sorting changes. Driving this
  // from an effect (instead of fetching inside each handler) means one
  // interaction that updates several pieces of state at once — e.g. changing
  // the page size also resets the page, and TablePagination additionally fires
  // onPageChange(1) — collapses into a single render, hence a single request
  // with the final values, avoiding the duplicate/stale fetch race.
  useEffect(() => {
    if (!isSearched) return;
    const onboardingCompleted = selectedOnboardingStatus === ""
      ? undefined
      : selectedOnboardingStatus === "completed";
    const hasMatchings = selectedMatchingStatus === ""
      ? undefined
      : selectedMatchingStatus === "with";
    searchClients(searchTerm, page, pageSize, onboardingCompleted, undefined, sortField, sortDirection, hasMatchings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortField, sortDirection]);

  const sortOptions: SortOption[] = [
    { label: t("client.sort.name"), value: ClientsSortField.Name },
    { label: t("client.sort.email"), value: ClientsSortField.Email },
    { label: t("client.sort.company"), value: ClientsSortField.Company },
    { label: t("client.sort.jobTitle"), value: ClientsSortField.JobTitle },
    { label: t("client.sort.onboarding"), value: ClientsSortField.Onboarding },
    { label: t("client.sort.createdAt"), value: ClientsSortField.CreatedAt },
  ];

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && canSearch) {
      handleSearch();
    }
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedOnboardingStatus !== "") count++;
    if (selectedMatchingStatus !== "") count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  if (isLoading && !isSearched) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress sx={{ color: zoullooColors.primary.main }} />
      </Box>
    );
  }

  if (error && !isSearching) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t("common.errorLoading")}: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box px={6} py={4}>
      {/* Header */}
      <Box mb={6}>
        <Typography variant="h2">{t("client.clientList")}</Typography>
        <Typography variant="body1" color="text.secondary" mt={1}>
          {t("client.showing")} {totalCount} {t("client.clients")}
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Search field */}
          <Box sx={{ flex: "1 1 auto" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("client.searchClients")}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSearchError("");
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { height: "40px" },
              }}
              error={!!searchError}
              helperText={searchError}
              size="small"
            />
          </Box>

          {/* Button group */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {/* Filters button */}
            <Button
              variant="outlined"
              onClick={handleToggleFilters}
              startIcon={
                activeFiltersCount > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: zoullooColors.primary.main,
                      color: zoullooColors.text.white,
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                    }}
                  >
                    {activeFiltersCount}
                  </Box>
                ) : null
              }
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                height: "40px",
                color: zoullooColors.secondary.main,
                borderColor: zoullooColors.secondary.main,
                ...(showFilters && {
                  backgroundColor: zoullooColors.grey[100],
                  borderColor: zoullooColors.secondary.main,
                  "&:hover": {
                    backgroundColor: zoullooColors.grey[200],
                    borderColor: zoullooColors.secondary.dark,
                  },
                }),
                "&:hover": {
                  borderColor: zoullooColors.secondary.main,
                },
              }}
            >
              {t("common.filters")}
            </Button>

            {/* Search button */}
            <Tooltip
              title={!canSearch ? t("client.enterSearchOrSelectFilter") : ""}
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  disabled={isSearching || !canSearch}
                  sx={{
                    height: "40px",
                    borderColor: zoullooColors.secondary.main,
                    color: zoullooColors.secondary.main,
                    backgroundColor: zoullooColors.background.default,
                    "&:hover": {
                      borderColor: zoullooColors.secondary.dark,
                      backgroundColor: zoullooColors.secondary.alpha,
                    },
                    opacity: !canSearch && !isSearching ? 0.7 : 1,
                  }}
                >
                  {isSearching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("common.search")
                  )}
                </Button>
              </span>
            </Tooltip>

            {/* Clear button */}
            {(isSearched || activeFiltersCount > 0) && (
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                sx={{
                  height: "40px",
                  borderColor: zoullooColors.secondary.main,
                  color: zoullooColors.secondary.main,
                  "&:hover": {
                    borderColor: zoullooColors.secondary.dark,
                    backgroundColor: zoullooColors.secondary.alpha,
                  },
                }}
              >
                {t("common.clear")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Box mt={2}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{ "&.Mui-focused": { color: zoullooColors.secondary.main } }}
                  >
                    {t("client.onboarding")}
                  </InputLabel>
                  <Select
                    value={selectedOnboardingStatus}
                    onChange={(e) => setSelectedOnboardingStatus(e.target.value)}
                    label={t("client.onboarding")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.border.main,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main,
                      },
                    }}
                  >
                    <MenuItem value="">{t("common.all")}</MenuItem>
                    <MenuItem value="completed">{t("client.onboardingCompleted")}</MenuItem>
                    <MenuItem value="pending">{t("client.onboardingPending")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{ "&.Mui-focused": { color: zoullooColors.secondary.main } }}
                  >
                    {t("client.matchedTalentsFilter")}
                  </InputLabel>
                  <Select
                    value={selectedMatchingStatus}
                    onChange={(e) => setSelectedMatchingStatus(e.target.value)}
                    label={t("client.matchedTalentsFilter")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.border.main,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main,
                      },
                    }}
                  >
                    <MenuItem value="">{t("common.all")}</MenuItem>
                    <MenuItem value="with">{t("client.matchedTalentsWith")}</MenuItem>
                    <MenuItem value="without">{t("client.matchedTalentsWithout")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Active filters */}
      <Box mb={3}>
        {/* Active filters display */}
        <Box>
          {activeFiltersCount > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t("common.activeFilters")}:
              </Typography>

              {selectedOnboardingStatus && (
                <Box
                  component="span"
                  sx={{
                    p: "4px 8px",
                    bgcolor: zoullooColors.secondary.main,
                    color: zoullooColors.text.white,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("client.onboarding")}:{" "}
                  {selectedOnboardingStatus === "completed"
                    ? t("client.onboardingCompleted")
                    : t("client.onboardingPending")}
                </Box>
              )}

              {selectedMatchingStatus && (
                <Box
                  component="span"
                  sx={{
                    p: "4px 8px",
                    bgcolor: zoullooColors.secondary.main,
                    color: zoullooColors.text.white,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("client.matchedTalentsFilter")}:{" "}
                  {selectedMatchingStatus === "with"
                    ? t("client.matchedTalentsWith")
                    : t("client.matchedTalentsWithout")}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {isSearching && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {isSearched && !isSearching && clients.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("client.noSearchResults", { searchTerm })}
        </Alert>
      )}

      {/* Table */}
      {!isSearching && (
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("client.name")}</TableCell>
                <TableCell>{t("client.email")}</TableCell>
                <TableCell>{t("client.company")}</TableCell>
                <TableCell>{t("client.jobTitle")}</TableCell>
                <TableCell>{t("client.onboarding")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={8}>
                      <Typography variant="h6" gutterBottom>
                        {isSearched
                          ? t("client.noSearchResults", { searchTerm })
                          : t("client.noClientsFound")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.userId}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar>
                          {client.user?.firstName?.[0]}
                          {client.user?.lastName?.[0]}
                        </Avatar>
                        <Typography>
                          {client.user?.firstName} {client.user?.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{client.user?.email}</TableCell>
                    <TableCell>{client.company?.legalName || "-"}</TableCell>
                    <TableCell>{client.jobTitle || "-"}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: client.onboardingCompleted
                            ? zoullooColors.success.main
                            : zoullooColors.warning.main,
                          fontWeight: 500,
                        }}
                      >
                        {client.onboardingCompleted
                          ? t("client.onboardingCompleted")
                          : t("client.onboardingPending")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewClient(client.userId)}
                        color="secondary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClient(client.userId)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination with sort */}
      {totalPages > 0 && !isSearching && (
        <TablePagination
          currentPage={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 20, 50]}
          sortOptions={sortOptions}
          currentSort={currentSort}
          onSortChange={handleSortChange}
          disabled={false}
          sticky={true}
          mx={0}
        />
      )}
    </Box>
  );
};

export default ClientsList;
